import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { FractionationChannel, FractionationRoute } from 'src/app/models/Fractionation';
import { ProviderAccount } from 'src/app/models/ProviderAccount';
import { ProviderAccountService } from 'src/app/shared/services/provider-account.service';
import { ConfirmationService } from 'src/app/shared/components/confirmation-modal/confirmation.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { FractionationService } from 'src/app/shared/services/fractionations.service';
import { User } from './../../models/User';
import { PaginatedResult } from './../../shared/model/paginated-result';
import { AuthService } from './../../shared/services/auth.service';
import { ADMINISTRATOR } from './../../shared/services/user.service';

/** Canais das tabs, na ordem da tela. */
export const FRACTIONATION_CHANNELS: { value: FractionationChannel; label: string }[] = [
  { value: 'sms', label: 'SMS' },
  { value: 'rcs', label: 'RCS' },
];

/**
 * Fracionamentos (Admin › Operação), com tabs SMS | RCS desde o RCS (17tm466frtx,
 * decisão "RCS fracionamento por canal" no vault).
 *
 * ## A tab SMS é a tela de antes
 *
 * Mesmas ações (monitorada, Pix, padrão, editar, ativar), mesmas mensagens. Só a
 * listagem passa `channel=sms`, que é o default do backend.
 *
 * ## Padrão, monitorada e Pix são por canal
 *
 * Desde a F2.8 o backend procura e troca o dono de cada papel dentro do canal da
 * rota: marcar uma rota RCS como padrão não tira o papel da rota padrão de SMS.
 * Por isso a tab RCS oferece as mesmas ações da tab SMS.
 *
 * O canal vive na query string (`?channel=rcs`) para a paginação, o voltar do
 * navegador e o link direto caírem na tab certa.
 *
 * ## Provedores RCS: só os liberados pelo dev (regra de 15/10)
 *
 * O fracionamento RCS é coisa só do Admin. A tab RCS mostra quais provedores RCS o time de
 * desenvolvimento liberou (hoje a PingooMobi): o backend só devolve esses em
 * `GET /provider-accounts?channel=rcs` e recusa outro no pivô. Não há botão de "liberar
 * provedor": provedor novo entra por código.
 *
 * ## Layout
 *
 * O da carteira (layout v2, CU-17tm466nzee): page-head, barra de busca e `m8v-row-card`. As
 * tabs, o aviso e os provedores liberados do RCS entram nele com o estilo do próprio componente,
 * sem a classe raiz `.rcs-admin` (o modelo de Preços dela brigaria com o v2).
 */
@Component({
  selector: 'app-fractionations',
  templateUrl: './fractionations.component.html',
  styleUrls: ['./fractionations.component.scss'],
})
export class FractionationsComponent implements OnInit, OnDestroy {
  readonly channels = FRACTIONATION_CHANNELS;

  channel: FractionationChannel = 'sms';
  page = 1;
  perPage = 10;
  result: PaginatedResult<FractionationRoute[]>;
  loading = false;
  filterText = '';
  subscriptions: Subscription[] = [];

  user: User;

  /** Contas RCS liberadas pelo dev (tab RCS). `null` = ainda carregando. */
  releasedProviders: ProviderAccount[] | null = null;

  get administrator() {
    return this.user?.profile_id === ADMINISTRATOR;
  }

  get isRcs(): boolean {
    return this.channel === 'rcs';
  }

  constructor(
    private confirmationService: ConfirmationService,
    private fractionationService: FractionationService,
    private toastService: ToastService,
    private router: Router,
    private authService: AuthService,
    private route: ActivatedRoute,
    private providerAccountService: ProviderAccountService
  ) {}

  ngOnInit() {
    this.user = this.authService.getUser();

    if (!this.administrator) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.subscriptions.push(
      this.route.queryParams.subscribe((params) => {
        this.channel = params.channel === 'rcs' ? 'rcs' : 'sms';

        if (this.isRcs && this.releasedProviders === null) {
          this.loadReleasedProviders();
        }

        if (!params.page) {
          this.pageChanged(1);
          return;
        }

        this.page = +params.page;
        this.perPage = +params.per_page || this.perPage;
        this.listPaginated();
      })
    );
  }

  private loadReleasedProviders(): void {
    this.subscriptions.push(
      this.providerAccountService.listAll('rcs').subscribe({
        next: (accounts) => (this.releasedProviders = accounts || []),
        error: () => (this.releasedProviders = []),
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  selectChannel(channel: FractionationChannel): void {
    if (channel === this.channel) {
      return;
    }

    this.filterText = '';
    this.channel = channel;
    this.pageChanged(1);
  }

  pageChanged(page: number) {
    const queryParams = { page, per_page: this.perPage, channel: this.channel };

    this.router.navigate(['/fractionations'], { queryParams });
  }

  listPaginated() {
    this.loading = true;
    this.subscriptions.push(
      this.fractionationService
        .listPaginated(this.page, this.perPage, this.filterText, this.channel)
        .subscribe({
          next: (resultData) => {
            this.loading = false;
            this.result = resultData;
          },
          error: () => {
            this.loading = false;
            this.toastService.error('Não foi possível carregar as rotas.');
          },
        })
    );
  }

  changeGroupActive(route: FractionationRoute) {
    route.active = !route.active;
    this.fractionationService.changeGroupActive(route).subscribe({
      next: () => this.listPaginated(),
      error: () => {
        route.active = !route.active;
        this.toastService.error('Erro ao desativar a rota');
      },
    });
  }

  redirectToEdit(route: FractionationRoute) {
    this.router.navigate([`/fractionations/${route.id}`]);
  }

  turnDefaultRoute(route: FractionationRoute) {
    this.confirmationService.show(`Tem certeza que deseja configurar a rota "${route.name}" para o <b>PADRÃO DE TODO O SISTEMA</b>?`, () => {
      this.fractionationService.turnDefaultRoute(route).subscribe({
        next: () => {
          this.toastService.success('Rota padrão alterada com sucesso!');
          this.listPaginated();
        },
        error: (error) => this.toastService.error(error.error?.message || 'Erro ao alterar a rota padrão'),
      });
    });
  }

  turnMonitoredRoute(route: FractionationRoute) {
    this.confirmationService.show(`Tem certeza que deseja configurar a rota "${route.name}" para o <b>MONITORADA DE TODO O SISTEMA</b>?`, () => {
      this.fractionationService.turnMonitoredRoute(route).subscribe({
        next: () => {
          this.toastService.success('Rota monitorada alterada com sucesso!');
          this.listPaginated();
        },
        error: (error) => this.toastService.error(error.error?.message || 'Erro ao alterar a rota monitorada'),
      });
    });
  }

  turnPixRoute(route: FractionationRoute) {
    this.confirmationService.show(`Tem certeza que deseja configurar a rota "${route.name}" para o <b>PIX DE TODO O SISTEMA</b>?`, () => {
      this.fractionationService.turnPixRoute(route).subscribe({
        next: () => {
          this.toastService.success('Rota pix alterada com sucesso!');
          this.listPaginated();
        },
        error: (error) => this.toastService.error(error.error?.message || 'Erro ao alterar a rota Pix'),
      });
    });
  }

  searchRoutes() {
    this.page = 1;
    this.listPaginated();
  }

  newRoute() {
    this.router.navigate(['/fractionations/new'], { queryParams: { channel: this.channel } });
  }

  trackByRouteId(_index: number, route: FractionationRoute): string {
    return route.id;
  }
}
